import { useEffect, useState } from 'react'
import { Copy, Send, Trash2 } from 'lucide-react'
import { changeAccountantAccess, inviteAccountant, listAccountants, removeAccountant } from '../../api/practice'
import ErrorBanner from '../../components/ErrorBanner'
import HelpTip from '../../components/HelpTip'
import Spinner from '../../components/Spinner'
import { StatusBadge } from '../../components/PageParts'
import { useToast } from '../../components/Toast.jsx'
import { fmtDay } from '../../utils/format'
import { Field } from '../master/masterShared'
import { initials, logoSrc } from '../practice/practiceShared'
import { SettingsHeader, SettingsTabs } from './settingsShared'

const ACCESS = [
  { id: 'full', label: 'Full books', sub: 'Entries, vendor invoices, month-end' },
  { id: 'view', label: 'View only', sub: 'Reports and ledgers, no changes' },
]
const ACCESS_HELP = 'Full books: your accountant can post journal entries, match vendor invoices, reconcile the bank and close the month, like a user with the Accountant role. View only: they can read your books and reports but can’t change anything. They never see your Users, Plan or Settings, and you can change this or remove them at any time.'

function AccessPick({ value, onChange, disabled, compact }) {
  return (
    <div className={`segmented${compact ? ' compact' : ''}`} role="radiogroup" aria-label="Access">
      {ACCESS.map((a) => (
        <button key={a.id} type="button" role="radio" aria-checked={value === a.id} className={value === a.id ? 'active' : ''}
          disabled={disabled} onClick={() => value !== a.id && onChange(a.id)}>
          {a.label}{!compact && <small>{a.sub}</small>}
        </button>
      ))}
    </div>
  )
}

/** The company's accountants: invite one by email, choose what they can do, remove them. */
export default function AccountantSettings() {
  const { notify } = useToast()
  const [rows, setRows] = useState(null)
  const [error, setError] = useState('')
  const [email, setEmail] = useState('')
  const [access, setAccess] = useState('full')
  const [fields, setFields] = useState({})
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(null)
  const [busyId, setBusyId] = useState(null)

  const load = () => listAccountants().then(setRows).catch((e) => { setError(e.message || 'Couldn’t load your accountants.'); setRows([]) })
  useEffect(() => { load() }, [])

  const send = async (e) => {
    e && e.preventDefault()
    setSending(true); setError(''); setFields({}); setSent(null)
    try {
      const r = await inviteAccountant({ email, access })
      setSent({ ...r, email: email.trim().toLowerCase() })
      setEmail('')
      notify(r.email_sent ? 'Invite sent' : 'Invite ready to share')
      load()
    } catch (err) {
      setFields(err.fields || {})
      setError(Object.keys(err.fields || {}).length ? '' : (err.message || 'Couldn’t send the invite.'))
    } finally { setSending(false) }
  }
  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); notify('Link copied') } catch { window.prompt('Copy the invite link:', text) }
  }
  const change = async (row, next) => {
    setBusyId(row.link_id); setError('')
    try { await changeAccountantAccess(row.link_id, next); notify(`${row.practice_name || row.email}: ${ACCESS.find((a) => a.id === next).label}`); await load() } catch (err) { setError(err.message || 'Couldn’t change their access.') }
    setBusyId(null)
  }
  const remove = async (row) => {
    const invited = row.status === 'invited'
    const ask = invited
      ? `Cancel the invite to ${row.email}? The link stops working.`
      : `Remove ${row.practice_name} as your accountant? They lose access at once; your books are not changed.`
    if (!window.confirm(ask)) return
    setBusyId(row.link_id); setError('')
    try { await removeAccountant(row.link_id); notify(invited ? 'Invite cancelled' : 'Accountant removed'); await load() } catch (err) { setError(err.message || 'Couldn’t remove them.') }
    setBusyId(null)
  }

  const active = (rows || []).filter((r) => r.status === 'active')
  const invited = (rows || []).filter((r) => r.status === 'invited')
  return (
    <div className="ci-page">
      <SettingsTabs />
      <SettingsHeader title="Accountant" meta="Let your accountant work in your books from their own ASYS practice. Accountants don’t count toward your plan’s users." />
      {error && <ErrorBanner message={error} />}

      <div className="set-stack">
        <form className="ci-form-meta-card mf-form" onSubmit={send} noValidate>
          <div className="mf-card-title">Invite your accountant <HelpTip text={ACCESS_HELP} /></div>
          <div className="ci-form-meta-top two">
            <Field label="Their email" error={fields.email} hint="We email them a link. If they don’t use ASYS yet, they can make a free practice account from it.">
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@firm.com" autoComplete="off" />
            </Field>
            <div className="ci-form-field">
              <span>What they can do</span>
              <AccessPick value={access} onChange={setAccess} />
              {fields.access && <span className="acc-field-error">{fields.access}</span>}
            </div>
          </div>
          <div className="set-actions acct-actions">
            <button type="submit" className="ci-btn ci-btn-primary" disabled={sending}><Send size={14} />{sending ? 'Sending…' : 'Send invite'}</button>
          </div>
          {sent && (
            <div className="acct-link">
              <p className="doc-hint">
                {sent.email_sent ? `We emailed the invite to ${sent.email}. ` : `We couldn’t email ${sent.email}, so send them this link yourself. `}
                You can also share the link another way (WhatsApp, text); it works for 14 days, once.
              </p>
              <div className="acct-link-row">
                <input readOnly value={sent.invite_link} onFocus={(e) => e.target.select()} aria-label="Invite link" />
                <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={() => copy(sent.invite_link)}><Copy size={14} />Copy</button>
              </div>
            </div>
          )}
        </form>

        <div className="ci-form-meta-card mf-form acct-card">
          <div className="mf-card-title">Your accountants</div>
          {rows === null ? <Spinner label="Loading…" /> : rows.length === 0 ? (
            <p className="doc-hint">No accountant yet. When yours accepts an invite, their practice shows here.</p>
          ) : (
            <div className="acct-list">
              {active.map((r) => (
                <div key={r.link_id} className="acct-row">
                  {r.practice_logo ? <img className="pr-logo acct-logo" src={logoSrc(r.practice_logo)} alt="" /> : <span className="pr-logo pr-logo-text acct-logo">{initials(r.practice_name)}</span>}
                  <div className="acct-row-main">
                    <strong>{r.practice_name}</strong>
                    <span className="cell-sub">
                      {[r.email, r.since && `since ${fmtDay(r.since)}`].filter(Boolean).join(' · ')}
                    </span>
                    {r.people.length > 0 && <span className="cell-sub">Has opened your books: {r.people.join(', ')}</span>}
                  </div>
                  <AccessPick compact value={r.access} disabled={busyId === r.link_id} onChange={(next) => change(r, next)} />
                  <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm ci-btn-danger" disabled={busyId === r.link_id} onClick={() => remove(r)}><Trash2 size={14} />Remove</button>
                </div>
              ))}
              {invited.map((r) => (
                <div key={r.link_id} className="acct-row">
                  <div className="acct-row-main">
                    <strong>{r.email}</strong>
                    <span className="cell-sub">
                      {r.access_label} · invited {fmtDay(r.invited_on)}
                    </span>
                  </div>
                  {r.expired ? <StatusBadge label="Invite expired" tone="muted" /> : <StatusBadge label="Waiting to accept" tone="warn" />}
                  <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm ci-btn-danger" disabled={busyId === r.link_id} onClick={() => remove(r)}><Trash2 size={14} />Cancel</button>
                </div>
              ))}
            </div>
          )}
          {invited.some((r) => r.expired) && <p className="doc-hint">To send a new link, invite the same email again.</p>}
        </div>
      </div>
    </div>
  )
}
